import { useQuery } from "@tanstack/react-query";
import { lpBalance, dexRemoveLiquidity } from "@/lib/dex";
import { Button } from "@/components/ui/button";
import { fmtAmt } from "@/components/dex/DexParts";

const PCTS = [25, 50, 100];
const label = (p) => `${p.token0.symbol}/${p.token1.symbol}`;

const PositionRow = ({ p, bal, onRemove, busy, i }) => {
  const share = Number(bal) / Number(p.totalSupplyRaw);
  return (
    <div data-testid={`dex-position-${i}`} className="p-4 rounded-xl border border-[#2A2411] bg-[#050505] space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-display font-bold text-white">{label(p)}</p>
        <p className="text-xs text-zinc-500">{fmtAmt(share * 100, 4)}% of pool</p>
      </div>
      <p data-testid={`dex-position-amounts-${i}`} className="text-sm font-mono text-zinc-300">
        {fmtAmt(p.reserve0 * share)} {p.token0.symbol} + {fmtAmt(p.reserve1 * share)} {p.token1.symbol}
      </p>
      <div className="flex gap-2">
        {PCTS.map((pct) => (
          <Button key={pct} data-testid={`dex-remove-${i}-${pct}`} size="sm" variant="outline" disabled={busy} onClick={() => onRemove(p, (bal * BigInt(pct)) / 100n, pct)}
            className="border-[#2A2411] bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10">Withdraw {pct}%</Button>
        ))}
      </div>
    </div>
  );
};

export const DexPositions = ({ dex, address, run, busy }) => {
  const { pairs, router } = dex;
  const owner = address.account;
  const { data: balances, refetch } = useQuery({
    queryKey: ["dexLp", owner, pairs.map((p) => p.address + p.totalSupplyRaw).join()],
    queryFn: () => Promise.all(pairs.map((p) => lpBalance(p.address, owner))),
    enabled: !!owner && pairs.length > 0,
  });
  if (!owner) return <p data-testid="dex-positions-connect" className="text-sm text-zinc-500">Connect your wallet to see your liquidity.</p>;
  const mine = pairs.map((p, idx) => ({ p, bal: balances?.[idx] || 0n })).filter((x) => x.bal > 0n);
  const onRemove = (p, liquidity, pct) => run((onStep) => dexRemoveLiquidity({ eip1193: address.eip1193, routerAddr: router, pair: p, liquidity, onStep })
    .then((h) => { refetch(); return h; }), `Withdrew ${pct}% from ${label(p)}`);
  return (
    <div data-testid="dex-positions" className="space-y-3">
      {balances && mine.length === 0 && <p data-testid="dex-positions-empty" className="text-sm text-zinc-500">You have no liquidity yet.</p>}
      {mine.map((x, i) => <PositionRow key={x.p.address} p={x.p} bal={x.bal} onRemove={onRemove} busy={busy} i={i} />)}
    </div>
  );
};

export const PairsTable = ({ pairs }) => (
  <div data-testid="dex-pairs" className="p-5 rounded-xl border border-[#2A2411] bg-[#0A0A0A]">
    <p className="text-sm font-medium text-white mb-3">All pairs ({pairs.length})</p>
    <div className="space-y-2">
      {pairs.map((p, i) => (
        <div key={p.address} data-testid={`dex-pair-row-${i}`} className="flex items-center justify-between gap-3 text-sm py-2 border-b border-[#2A2411] last:border-0">
          <span className="font-display font-bold text-white">{label(p)}</span>
          <span className="font-mono text-zinc-400 text-xs text-right">{fmtAmt(p.reserve0, 4)} {p.token0.symbol} · {fmtAmt(p.reserve1, 4)} {p.token1.symbol}</span>
        </div>
      ))}
    </div>
  </div>
);
